import React, { useState, useEffect } from 'react';
import { X, Star, Trash2, ChevronLeft, ChevronRight, Loader2, ImageIcon } from 'lucide-react';
import axios from 'axios';

function ImageGalleryModal({ productId, productTitle, onClose, onImagesChanged }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [settingPrimary, setSettingPrimary] = useState(null);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchImages();
  }, [productId]);

  async function fetchImages() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${baseUrl}/api/products/${productId}/images`);
      setImages(data.images);
      
      // Set current index to primary image if exists
      const primaryIndex = data.images.findIndex(img => img.is_primary);
      if (primaryIndex !== -1) {
        setCurrentIndex(primaryIndex);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      alert('Error al cargar las imágenes');
    } finally {
      setLoading(false);
    }
  }

  async function handleSetPrimary(imageId, index) {
    if (settingPrimary) return;
    
    setSettingPrimary(imageId);
    try {
      await axios.put(`${baseUrl}/api/products/${productId}/images/${imageId}/set-primary`);
      
      // Update local state
      setImages(images.map(img => ({
        ...img,
        is_primary: img.id === imageId
      })));
      
      onImagesChanged();
    } catch (error) {
      console.error('Error setting primary image:', error);
      alert(error.response?.data?.error || 'Error al establecer imagen principal');
    } finally {
      setSettingPrimary(null);
    }
  }

  async function handleDelete(imageId, index) {
    if (!window.confirm('¿Estás seguro de eliminar esta imagen?')) return;
    
    setDeleting(imageId);
    try {
      await axios.delete(`${baseUrl}/api/products/${productId}/images/${imageId}`);
      
      const newImages = images.filter(img => img.id !== imageId);
      setImages(newImages);
      
      // Adjust current index if needed
      if (currentIndex >= newImages.length) {
        setCurrentIndex(Math.max(0, newImages.length - 1));
      }
      
      onImagesChanged();
    } catch (error) {
      console.error('Error deleting image:', error);
      alert(error.response?.data?.error || 'Error al eliminar la imagen');
    } finally {
      setDeleting(null);
    }
  }

  function goToPrevious() {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }

  function goToNext() {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }

  const currentImage = images[currentIndex];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-slate-900 font-montserrat">
              Galería de Imágenes
            </h2>
            <p className="text-sm text-slate-600 font-montserrat mt-1">
              {productTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-12 h-12 text-brand-primary animate-spin" />
            </div>
          ) : images.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-12">
              <div className="text-center">
                <ImageIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                  No hay imágenes
                </h3>
                <p className="text-sm text-slate-600 font-montserrat">
                  Este producto aún no tiene imágenes
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Main Image Carousel */}
              <div className="flex-1 relative bg-slate-100 flex flex-col min-h-0">
                {/* Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={goToPrevious}
                      className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors z-10"
                    >
                      <ChevronLeft className="w-6 h-6 text-slate-900" />
                    </button>
                    <button
                      onClick={goToNext}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors z-10"
                    >
                      <ChevronRight className="w-6 h-6 text-slate-900" />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1.5 rounded-lg text-sm font-montserrat z-10">
                  {currentIndex + 1} / {images.length}
                </div>

                {/* Primary Badge */}
                {currentImage?.is_primary && (
                  <div className="absolute top-4 left-4 bg-brand-primary text-white px-3 py-1.5 rounded-lg text-sm font-bold font-montserrat flex items-center gap-2 z-10">
                    <Star className="w-4 h-4 fill-current" />
                    Imagen Principal
                  </div>
                )}

                {/* Image Container - with max height constraint */}
                <div className="flex-1 flex items-center justify-center p-8 pb-20 overflow-hidden">
                  <img
                    src={currentImage?.url}
                    alt={currentImage?.alt_text || 'Product image'}
                    className="max-w-full max-h-full object-contain rounded-lg shadow-xl"
                    style={{ maxHeight: 'calc(100% - 80px)' }}
                  />
                </div>

                {/* Action Buttons - Fixed at bottom with white background */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white to-transparent pt-8 pb-4 px-4 flex justify-center gap-3">
                  {!currentImage?.is_primary && (
                    <button
                      onClick={() => handleSetPrimary(currentImage?.id, currentIndex)}
                      disabled={settingPrimary === currentImage?.id}
                      className="px-4 py-2 bg-white hover:bg-brand-primary hover:text-white border-2 border-brand-primary text-brand-primary rounded-lg font-semibold font-montserrat transition-colors flex items-center gap-2 disabled:opacity-50 shadow-lg"
                    >
                      {settingPrimary === currentImage?.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Estableciendo...
                        </>
                      ) : (
                        <>
                          <Star className="w-4 h-4" />
                          Marcar como Principal
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(currentImage?.id, currentIndex)}
                    disabled={deleting === currentImage?.id || images.length === 1}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold font-montserrat transition-colors flex items-center gap-2 disabled:opacity-50 shadow-lg"
                    title={images.length === 1 ? 'No puedes eliminar la única imagen' : ''}
                  >
                    {deleting === currentImage?.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Eliminando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Eliminar
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Thumbnail Strip */}
              <div className="bg-slate-50 border-t border-slate-200 p-4 flex-shrink-0">
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
                  {images.map((img, index) => (
                    <button
                      key={img.id}
                      onClick={() => setCurrentIndex(index)}
                      className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        currentIndex === index
                          ? 'border-brand-primary ring-2 ring-brand-primary/20'
                          : 'border-slate-300 hover:border-brand-primary/50'
                      }`}
                    >
                      <img
                        src={img.url}
                        alt={img.alt_text || `Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {img.is_primary && (
                        <div className="absolute top-1 right-1 bg-brand-primary text-white p-1 rounded">
                          <Star className="w-3 h-3 fill-current" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImageGalleryModal;